import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, photoUrl } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { SURVIVAL } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";
import {
  Sprout, Plus, ArrowLeft, Trees, Leaf, Flower2, Camera, Trash2, Calendar, AlertTriangle,
  CheckCircle2, Clock, PenLine, ShieldAlert, MapPin,
} from "lucide-react";
import { toast } from "sonner";

const TONE = {
  good: "bg-emerald-100 text-emerald-900 border-emerald-300",
  warn: "bg-amber-100 text-amber-900 border-amber-300",
  bad: "bg-rose-100 text-rose-900 border-rose-300",
};
const CAT_META = {
  trees: { label: "Canopy Trees", icon: Trees },
  shrubs: { label: "Shrubs / Understory", icon: Leaf },
  groundcover: { label: "Groundcover / Turf", icon: Flower2 },
};
const LOSS_CAUSES = [
  { v: "horticultural", l: "Horticultural stress" },
  { v: "environmental", l: "Environmental trigger" },
  { v: "vandalism_pests", l: "Vandalism / pests" },
];
const lossLabel = (v) => LOSS_CAUSES.find((c) => c.v === v)?.l || v;

const pct = (v) => (v == null ? "—" : `${v}%`);
const emptyBaseline = { trees_planted: "", shrubs_planted: "", groundcover_area: "", groundcover_target_pct: 90, source: "" };

function TierBadge({ tier, tone, testid }) {
  if (!tier) return <span className="text-xs text-muted-foreground">—</span>;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${TONE[tone] || TONE.warn}`} data-testid={testid}>{tier}</span>;
}

function DueBadge({ due }) {
  const map = {
    overdue: { c: "bg-rose-100 text-rose-800 border-rose-300", t: "Audit overdue", I: AlertTriangle },
    ok: { c: "bg-emerald-50 text-emerald-800 border-emerald-200", t: "On schedule", I: CheckCircle2 },
    due: { c: "bg-amber-100 text-amber-900 border-amber-300", t: "Audit due", I: Clock },
    no_audits: { c: "bg-amber-100 text-amber-900 border-amber-300", t: "No audits yet", I: Clock },
  };
  const m = map[due?.status] || map.ok;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${m.c}`}>
      <m.I className="h-3 w-3" /> {m.t}{due?.next_due ? ` · next ${due.next_due}` : ""}
    </span>
  );
}

// ---------------- Register card (list) ----------------
function RegisterCard({ r, onOpen }) {
  const lat = r.latest || {};
  return (
    <button onClick={() => onOpen(r.id)} data-testid={SURVIVAL.registerCard(r.id)}
      className="group rounded-xl border border-sage-light bg-white p-4 text-left transition-all hover:border-forest-mid hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-heading text-base font-bold text-forest-dark">{r.zone_name}</p>
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><MapPin className="h-3 w-3" /> {r.site_name}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-heading text-2xl font-bold text-forest-dark">{pct(lat.overall)}</p>
          <TierBadge tier={lat.overall_tier} tone={lat.overall_tone} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <DueBadge due={r.due} />
        {r.is_critical && <span className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800"><ShieldAlert className="h-3 w-3" /> Critical</span>}
        <span className="text-[11px] text-muted-foreground">{r.audit_count} audit{r.audit_count === 1 ? "" : "s"}</span>
      </div>
    </button>
  );
}

// ---------------- New / edit register dialog ----------------
function RegisterDialog({ open, onOpenChange, ws, editReg, onSaved }) {
  const isEdit = !!editReg;
  const [zoneId, setZoneId] = useState("");
  const [form, setForm] = useState({ planting_start: new Date().toISOString().slice(0, 10), establishment_months: 12, audit_cadence_days: 90, ...emptyBaseline });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editReg) {
      const b = editReg.register?.baseline || editReg.baseline || {};
      setZoneId(editReg.register?.zone_id || editReg.zone_id || "");
      setForm({
        planting_start: (editReg.register?.planting_start || editReg.planting_start || "").slice(0, 10),
        establishment_months: editReg.register?.establishment_months || editReg.establishment_months || 12,
        audit_cadence_days: editReg.register?.audit_cadence_days || editReg.audit_cadence_days || 90,
        trees_planted: b.trees?.planted ?? "", shrubs_planted: b.shrubs?.planted ?? "",
        groundcover_area: b.groundcover?.planted_area ?? "", groundcover_target_pct: b.groundcover?.target_coverage_pct ?? 90,
        source: b.source ?? "",
      });
    } else {
      setZoneId(ws.zoneId || "");
      setForm({ planting_start: new Date().toISOString().slice(0, 10), establishment_months: 12, audit_cadence_days: 90, ...emptyBaseline });
    }
  }, [open, editReg, ws.zoneId]);

  const num = (v) => (v === "" || v == null ? null : Number(v));
  const save = async () => {
    if (!isEdit && !zoneId) { toast.error("Choose a zone for this register"); return; }
    setBusy(true);
    const baseline = {
      trees_planted: num(form.trees_planted), shrubs_planted: num(form.shrubs_planted),
      groundcover_area: num(form.groundcover_area),
      groundcover_target_pct: num(form.groundcover_target_pct) ?? 90, source: form.source || null,
    };
    try {
      if (isEdit) {
        await api.patch(`/survival/registers/${editReg.register.id}`, {
          planting_start: form.planting_start, establishment_months: Number(form.establishment_months),
          audit_cadence_days: Number(form.audit_cadence_days), baseline,
        });
        toast.success("Baseline updated");
      } else {
        await api.post("/survival/registers", {
          contract_id: ws.contractId || null, site_id: ws.siteId, zone_id: zoneId,
          planting_start: form.planting_start, establishment_months: Number(form.establishment_months),
          audit_cadence_days: Number(form.audit_cadence_days), baseline,
        });
        toast.success("Register created");
      }
      onOpenChange(false);
      await onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid={SURVIVAL.dialog}>
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit planted baseline" : "New plant survival register"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <div className="space-y-1.5">
              <Label>Zone</Label>
              <Select value={zoneId} onValueChange={setZoneId}>
                <SelectTrigger data-testid={SURVIVAL.field("zone")}><SelectValue placeholder="Select a zone" /></SelectTrigger>
                <SelectContent>{ws.visibleZones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}</SelectContent>
              </Select>
              {ws.visibleZones.length === 0 && <p className="text-[11px] text-amber-700">This site has no zones — add zones in Setup & Data first.</p>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Planting / handover date</Label><Input type="date" value={form.planting_start} onChange={(e) => setForm({ ...form, planting_start: e.target.value })} data-testid={SURVIVAL.field("planting")} /></div>
            <div className="space-y-1.5"><Label>Establishment window</Label>
              <Select value={String(form.establishment_months)} onValueChange={(v) => setForm({ ...form, establishment_months: v })}>
                <SelectTrigger data-testid={SURVIVAL.field("window")}><SelectValue /></SelectTrigger>
                <SelectContent>{[12, 24, 36].map((m) => <SelectItem key={m} value={String(m)}>{m / 12} year{m > 12 ? "s" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Audit cadence</Label>
            <Select value={String(form.audit_cadence_days)} onValueChange={(v) => setForm({ ...form, audit_cadence_days: v })}>
              <SelectTrigger data-testid={SURVIVAL.field("cadence")}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Monthly</SelectItem>
                <SelectItem value="90">Quarterly</SelectItem>
                <SelectItem value="180">Half-yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-lg border border-sage-light bg-sage-surface/40 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Planted baseline (by category)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Canopy trees (count)</Label><Input type="number" min="0" value={form.trees_planted} onChange={(e) => setForm({ ...form, trees_planted: e.target.value })} data-testid={SURVIVAL.field("trees")} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Shrubs / understory (count)</Label><Input type="number" min="0" value={form.shrubs_planted} onChange={(e) => setForm({ ...form, shrubs_planted: e.target.value })} data-testid={SURVIVAL.field("shrubs")} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Groundcover / turf (m²)</Label><Input type="number" min="0" value={form.groundcover_area} onChange={(e) => setForm({ ...form, groundcover_area: e.target.value })} data-testid={SURVIVAL.field("gc-area")} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Target coverage %</Label><Input type="number" min="0" max="100" value={form.groundcover_target_pct} onChange={(e) => setForm({ ...form, groundcover_target_pct: e.target.value })} data-testid={SURVIVAL.field("gc-target")} /></div>
            </div>
            <div className="mt-3 space-y-1.5"><Label className="text-xs">Baseline source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="As-built drawings / nursery manifest / invoice" data-testid={SURVIVAL.field("source")} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-forest-mid hover:bg-forest-dark" disabled={busy} onClick={save} data-testid={SURVIVAL.saveBtn}>{isEdit ? "Save baseline" : "Create register"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Log audit form ----------------
function AuditForm({ regId, onLogged }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ date: today, method: "census", sample_pct: "", inspector: "", trees_living: "", shrubs_living: "", groundcover_coverage_pct: "", notes: "", loss_cause: "none" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const pickPhoto = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setUploading(true);
    for (const file of picked) {
      try {
        const dataUrl = await compressImage(file);
        const r = await api.post("/photos", { data_url: dataUrl, caption: "Survival audit" });
        setFiles((prev) => [...prev, r.data]);
      } catch (err) { toast.error(apiErr(err)); }
    }
    setUploading(false);
  };

  const num = (v) => (v === "" ? null : Number(v));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/survival/registers/${regId}/audits`, {
        date: f.date, method: f.method, sample_pct: f.method === "sampling" ? num(f.sample_pct) : null,
        inspector: f.inspector || null, notes: f.notes || null,
        loss_cause: f.loss_cause === "none" ? null : f.loss_cause,
        trees_living: num(f.trees_living), shrubs_living: num(f.shrubs_living),
        groundcover_coverage_pct: num(f.groundcover_coverage_pct), photos: files,
      });
      toast.success("Survival audit logged");
      setF({ date: today, method: "census", sample_pct: "", inspector: "", trees_living: "", shrubs_living: "", groundcover_coverage_pct: "", notes: "", loss_cause: "none" });
      setFiles([]);
      await onLogged();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-dashed border-sage-light bg-sage-surface/40 p-4">
      <p className="mb-3 flex items-center gap-1.5 font-heading text-sm font-bold text-forest-dark"><Plus className="h-4 w-4 text-forest-mid" /> Log a survival audit</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="space-y-1.5"><Label className="text-xs">Audit date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid={SURVIVAL.auditField("date")} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Method</Label>
          <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
            <SelectTrigger data-testid={SURVIVAL.auditField("method")}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="census">Full census</SelectItem><SelectItem value="sampling">Sampling</SelectItem></SelectContent>
          </Select>
        </div>
        {f.method === "sampling" && <div className="space-y-1.5"><Label className="text-xs">Sample %</Label><Input type="number" min="0" max="100" value={f.sample_pct} onChange={(e) => setF({ ...f, sample_pct: e.target.value })} data-testid={SURVIVAL.auditField("sample")} /></div>}
        <div className="space-y-1.5"><Label className="text-xs">Inspector</Label><Input value={f.inspector} onChange={(e) => setF({ ...f, inspector: e.target.value })} placeholder="Name" data-testid={SURVIVAL.auditField("inspector")} /></div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Trees living</Label><Input type="number" min="0" value={f.trees_living} onChange={(e) => setF({ ...f, trees_living: e.target.value })} data-testid={SURVIVAL.auditField("trees")} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Shrubs living</Label><Input type="number" min="0" value={f.shrubs_living} onChange={(e) => setF({ ...f, shrubs_living: e.target.value })} data-testid={SURVIVAL.auditField("shrubs")} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Groundcover coverage %</Label><Input type="number" min="0" max="100" value={f.groundcover_coverage_pct} onChange={(e) => setF({ ...f, groundcover_coverage_pct: e.target.value })} data-testid={SURVIVAL.auditField("gc")} /></div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label className="text-xs">Loss cause (optional)</Label>
          <Select value={f.loss_cause} onValueChange={(v) => setF({ ...f, loss_cause: v })}>
            <SelectTrigger data-testid={SURVIVAL.auditField("loss")}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">None</SelectItem>{LOSS_CAUSES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="On-site observation" data-testid={SURVIVAL.auditField("notes")} /></div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-sage-light bg-white px-2 py-1 text-[11px] font-medium text-forest-dark hover:bg-sage-light">
          <Camera className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Add photo"}
          <input type="file" accept="image/*" multiple className="hidden" onChange={pickPhoto} data-testid={SURVIVAL.auditPhoto} />
        </label>
        {files.map((p) => <img key={p.id} src={photoUrl(p)} alt="pending" className="h-9 w-9 rounded border border-sage-light object-cover" />)}
        <Button size="sm" className="ml-auto h-9 bg-forest-mid text-xs hover:bg-forest-dark" disabled={busy || uploading} onClick={submit} data-testid={SURVIVAL.auditAddBtn}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Log audit
        </Button>
      </div>
    </div>
  );
}

// ---------------- Audit card ----------------
function AuditCard({ a, canEdit, onDelete }) {
  const rt = a.rates || {};
  return (
    <div className="rounded-xl border border-sage-light bg-white p-3" data-testid={SURVIVAL.auditItem(a.id)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-forest-dark">
          <Calendar className="h-3.5 w-3.5 text-forest-mid" /> <b>{a.date}</b>
          <span className="rounded bg-sage-surface px-1.5 py-0.5 text-[10px] capitalize text-muted-foreground">{a.method}{a.method === "sampling" && a.sample_pct ? ` ${a.sample_pct}%` : ""}</span>
          <span className="font-heading text-base font-bold text-forest-dark">{pct(rt.overall)}</span>
          <TierBadge tier={rt.overall_tier} tone={rt.overall_tone} />
        </div>
        {canEdit && <button onClick={() => onDelete(a.id)} className="shrink-0 text-rose-500 hover:text-rose-700" title="Delete audit"><Trash2 className="h-4 w-4" /></button>}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {["trees", "shrubs", "groundcover"].map((k) => {
          const c = rt.categories?.[k] || {};
          const Meta = CAT_META[k];
          return (
            <div key={k} className="rounded-lg border border-sage-light bg-sage-surface/40 p-2 text-center">
              <p className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground"><Meta.icon className="h-3 w-3" /> {Meta.label}</p>
              <p className="font-heading text-sm font-bold text-forest-dark">{pct(c.rate)}</p>
              <TierBadge tier={c.tier} tone={c.tier_tone} />
            </div>
          );
        })}
      </div>
      {(a.inspector || a.loss_cause || a.notes) && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {a.inspector && <>Inspector <b className="text-forest-dark">{a.inspector}</b>. </>}
          {a.loss_cause && <span className="text-amber-700">Loss cause: {lossLabel(a.loss_cause)}. </span>}
          {a.notes && <span className="italic">“{a.notes}”</span>}
        </p>
      )}
      {(a.photos || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {a.photos.map((p) => <a key={p.id} href={photoUrl(p)} target="_blank" rel="noreferrer"><img src={photoUrl(p)} alt="audit" className="h-12 w-12 rounded border border-sage-light object-cover" /></a>)}
        </div>
      )}
    </div>
  );
}

// ---------------- Register detail ----------------
function RegisterDetail({ regId, onBack, onChanged }) {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [rem, setRem] = useState({ action: "", owner: "", due_date: "" });

  const load = useCallback(async () => {
    try { const r = await api.get(`/survival/registers/${regId}`); setD(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  }, [regId]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <p className="text-sm text-muted-foreground">Loading register…</p>;
  const reg = d.register;
  const lat = d.latest || {};
  const canEdit = d.can_edit;

  const delAudit = async (aid) => {
    try { await api.delete(`/survival/registers/${regId}/audits/${aid}`); await load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addRem = async () => {
    if (!rem.action.trim()) { toast.error("Describe the corrective action"); return; }
    try {
      await api.post(`/survival/registers/${regId}/remediation`, { action: rem.action, owner: rem.owner || null, due_date: rem.due_date || null });
      setRem({ action: "", owner: "", due_date: "" }); await load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleRem = async (item) => {
    try { await api.patch(`/survival/remediation/${item.id}`, { status: item.status === "resolved" ? "open" : "resolved" }); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const raiseDefect = async () => {
    try { await api.post(`/survival/registers/${regId}/raise-defect`); toast.success("Raised as a Diagnose defect"); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const base = reg.baseline || {};
  return (
    <div className="space-y-4" data-testid={SURVIVAL.detail}>
      <Button variant="ghost" size="sm" className="h-8 px-2 text-forest-dark" onClick={onBack} data-testid={SURVIVAL.backBtn}><ArrowLeft className="mr-1 h-4 w-4" /> All registers</Button>

      {/* summary */}
      <Card className="border-sage-light p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-bold text-forest-dark">{reg.zone_name}</h2>
            <p className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {reg.site_name}</p>
            <p className="mt-2 text-xs text-muted-foreground">Planted {reg.planting_start} · establishment window to {d.establishment_end || "—"} · {reg.audit_cadence_days}-day cadence</p>
            <div className="mt-2"><DueBadge due={d.due} /></div>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Latest overall survival</p>
            <p className="font-heading text-5xl font-bold text-forest-dark" data-testid={SURVIVAL.overall}>{pct(lat.overall)}</p>
            <TierBadge tier={lat.overall_tier} tone={lat.overall_tone} testid={SURVIVAL.tier} />
            <p className="mt-1 text-[11px] text-muted-foreground">vs baseline 100% at planting</p>
          </div>
        </div>
      </Card>

      {/* critical handling */}
      {d.is_critical && (
        <Card className="border-rose-300 bg-rose-50/60 p-4" data-testid={SURVIVAL.criticalFlag}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-rose-800"><ShieldAlert className="h-4 w-4" /> Critical survival — below 85%</p>
            {canEdit && (
              <Button size="sm" variant="outline" className="h-8 border-rose-300 text-xs text-rose-700 hover:bg-rose-100" onClick={raiseDefect} data-testid={SURVIVAL.raiseDefectBtn} disabled={!!reg.defect_id}>
                <AlertTriangle className="mr-1 h-3.5 w-3.5" /> {reg.defect_id ? "Defect raised" : "Raise as Diagnose defect"}
              </Button>
            )}
          </div>
          {/* remediation log */}
          <div className="mt-3 space-y-2">
            {(d.remediation || []).length === 0 && <p className="text-[11px] text-rose-700/80">No corrective actions logged yet.</p>}
            {(d.remediation || []).map((it) => (
              <div key={it.id} className="flex items-start justify-between gap-2 rounded-lg border border-rose-200 bg-white p-2 text-xs" data-testid={SURVIVAL.remedItem(it.id)}>
                <div className="min-w-0">
                  <p className="text-forest-dark">{it.action}</p>
                  <p className="text-[10px] text-muted-foreground">{it.owner ? `Owner ${it.owner}` : "Unassigned"}{it.due_date ? ` · due ${it.due_date}` : ""}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${it.status === "resolved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{it.status}</span>
                  {canEdit && <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => toggleRem(it)} data-testid={SURVIVAL.remedResolve(it.id)}>{it.status === "resolved" ? "Reopen" : "Resolve"}</Button>}
                </div>
              </div>
            ))}
            {canEdit && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-rose-200 bg-white p-2">
                <Input value={rem.action} onChange={(e) => setRem({ ...rem, action: e.target.value })} placeholder="Corrective action" className="h-8 flex-1 min-w-[160px] text-xs" data-testid={SURVIVAL.auditField("rem-action")} />
                <Input value={rem.owner} onChange={(e) => setRem({ ...rem, owner: e.target.value })} placeholder="Owner" className="h-8 w-28 text-xs" data-testid={SURVIVAL.auditField("rem-owner")} />
                <Input type="date" value={rem.due_date} onChange={(e) => setRem({ ...rem, due_date: e.target.value })} className="h-8 w-[140px] text-xs" data-testid={SURVIVAL.auditField("rem-due")} />
                <Button size="sm" className="h-8 bg-rose-600 text-xs hover:bg-rose-700" onClick={addRem} data-testid={SURVIVAL.remedAddBtn}><Plus className="mr-1 h-3.5 w-3.5" /> Add action</Button>
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* trend */}
        <Card className="border-sage-light p-4 lg:col-span-3" data-testid={SURVIVAL.trend}>
          <p className="mb-2 font-heading text-sm font-bold text-forest-dark">Survival trend</p>
          {d.trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={d.trend} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E6DD" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis domain={[60, 100]} tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <ReferenceLine y={95} stroke="#059669" strokeDasharray="4 4" label={{ value: "Excellent 95%", fontSize: 9, fill: "#059669", position: "insideTopRight" }} />
                <ReferenceLine y={85} stroke="#e11d48" strokeDasharray="4 4" label={{ value: "Critical 85%", fontSize: 9, fill: "#e11d48", position: "insideBottomRight" }} />
                <Line type="monotone" dataKey="overall" name="Overall %" stroke="#27523C" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="trees" name="Trees" stroke="#2f6b4f" strokeWidth={1} dot={false} strokeOpacity={0.5} />
                <Line type="monotone" dataKey="shrubs" name="Shrubs" stroke="#b07a16" strokeWidth={1} dot={false} strokeOpacity={0.5} />
                <Line type="monotone" dataKey="groundcover" name="Groundcover" stroke="#6b8f3a" strokeWidth={1} dot={false} strokeOpacity={0.5} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="py-10 text-center text-sm text-muted-foreground">Log an audit to start the trend.</p>}
        </Card>

        {/* baseline */}
        <Card className="border-sage-light p-4 lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <p className="font-heading text-sm font-bold text-forest-dark">Planted baseline</p>
            {canEdit && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditOpen(true)} data-testid={SURVIVAL.editBaselineBtn}><PenLine className="mr-1 h-3.5 w-3.5" /> Edit</Button>}
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-muted-foreground"><Trees className="h-3.5 w-3.5" /> Canopy trees</span><b className="text-forest-dark">{base.trees?.planted ?? "—"}</b></div>
            <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-muted-foreground"><Leaf className="h-3.5 w-3.5" /> Shrubs / understory</span><b className="text-forest-dark">{base.shrubs?.planted ?? "—"}</b></div>
            <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-muted-foreground"><Flower2 className="h-3.5 w-3.5" /> Groundcover / turf</span><b className="text-forest-dark">{base.groundcover?.planted_area != null ? `${base.groundcover.planted_area} m²` : "—"}</b></div>
            <div className="flex items-center justify-between text-xs text-muted-foreground"><span>Target coverage</span><span>{base.groundcover?.target_coverage_pct ?? 90}%</span></div>
          </div>
          {base.source && <p className="mt-2 border-t border-sage-light pt-2 text-[11px] text-muted-foreground">Source: {base.source}</p>}
        </Card>
      </div>

      {/* audits */}
      <div className="space-y-3">
        <p className="font-heading text-base font-semibold text-forest-dark">Survival audits</p>
        {canEdit && <AuditForm regId={regId} onLogged={() => { load(); onChanged?.(); }} />}
        {d.audits.length === 0 ? (
          <p className="text-sm text-muted-foreground">No audits logged yet.</p>
        ) : (
          <div className="space-y-2">{d.audits.map((a) => <AuditCard key={a.id} a={a} canEdit={canEdit} onDelete={delAudit} />)}</div>
        )}
      </div>

      <RegisterDialog open={editOpen} onOpenChange={setEditOpen} ws={{}} editReg={d} onSaved={() => { load(); onChanged?.(); }} />
    </div>
  );
}

// ---------------- Page ----------------
export default function SurvivalPage() {
  const ws = useWorkspace();
  const { hasRole } = useAuth();
  const canEdit = hasRole("tenant_admin", "contract_manager", "supervisor");
  const [registers, setRegisters] = useState([]);
  const [selId, setSelId] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!ws.siteId) { setRegisters([]); setLoading(false); return; }
    setLoading(true);
    try { const r = await api.get(`/survival/registers?site_id=${ws.siteId}`); setRegisters(r.data); }
    catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  }, [ws.siteId]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(
    () => (ws.zoneId ? registers.filter((r) => r.zone_id === ws.zoneId) : registers),
    [registers, ws.zoneId]);

  if (selId) return <div data-testid={SURVIVAL.root}><RegisterDetail regId={selId} onBack={() => { setSelId(""); load(); }} onChanged={load} /></div>;

  return (
    <div className="space-y-5" data-testid={SURVIVAL.root}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-forest-dark"><Sprout className="h-5 w-5 text-forest-mid" /> Plant Survival Register</h2>
          <p className="text-sm text-muted-foreground">Baseline inventory & dated survival audits · feeds the ROOTS Plant Survival (E01) score</p>
        </div>
        {canEdit && ws.siteId && (
          <Button size="sm" className="bg-forest-mid hover:bg-forest-dark" onClick={() => setNewOpen(true)} data-testid={SURVIVAL.newBtn}>
            <Plus className="mr-1 h-4 w-4" /> New register
          </Button>
        )}
      </div>

      {!ws.siteId ? (
        <Card className="border-dashed border-sage-light p-10 text-center text-sm text-muted-foreground">Select a site in the context bar to view its plant-survival registers.</Card>
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Loading registers…</p>
      ) : shown.length === 0 ? (
        <Card className="border-dashed border-sage-light p-10 text-center text-sm text-muted-foreground">
          No plant-survival registers for this {ws.zoneId ? "zone" : "site"} yet.{canEdit ? " Create one to set the planted baseline." : ""}
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((r) => <RegisterCard key={r.id} r={r} onOpen={setSelId} />)}
        </div>
      )}

      <RegisterDialog open={newOpen} onOpenChange={setNewOpen} ws={ws} editReg={null} onSaved={load} />
    </div>
  );
}
